import React, { useEffect, useState } from 'react';
import { getAdminStats, getPendingUsers, approveUser, rejectUser, getAllPayments } from '../services/api';
import toast from 'react-hot-toast';
import { FiUsers, FiDollarSign, FiClock, FiTrendingUp, FiCheck, FiX } from 'react-icons/fi';

const AdminDashboard = () => {
  const [tab, setTab]         = useState('pending');
  const [stats, setStats]     = useState(null);
  const [pending, setPending] = useState([]);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    getAdminStats().then(r => setStats(r.data)).catch(console.error);
    getPendingUsers().then(r => setPending(r.data)).catch(console.error);
    getAllPayments().then(r => setPayments(r.data)).catch(console.error);
  }, []);

  const handleApprove = async (id, name) => {
    try { await approveUser(id); setPending(p => p.filter(u => u._id !== id)); toast.success(`${name} approved!`); getAdminStats().then(r => setStats(r.data)); }
    catch { toast.error('Failed to approve'); }
  };

  const handleReject = async (id, name) => {
    try { await rejectUser(id); setPending(p => p.filter(u => u._id !== id)); toast.success(`${name} rejected.`); }
    catch { toast.error('Failed to reject'); }
  };

  const StatCard = ({ icon: Icon, label, value, color, bg }) => (
    <div style={{ background: 'white', borderRadius: '20px', padding: '24px', boxShadow: 'var(--shadow-sm)' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
        <span style={{ fontSize: '13px', fontWeight: 600, color: 'var(--gray)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{label}</span>
        <div style={{ width: '38px', height: '38px', borderRadius: '10px', background: bg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Icon size={18} color={color} />
        </div>
      </div>
      <div style={{ fontFamily: 'var(--font-display)', fontSize: '32px', fontWeight: 800 }}>{value ?? '—'}</div>
    </div>
  );

  return (
    <div style={{ background: 'var(--lighter)', minHeight: '100vh', padding: '32px 0' }}>
      <div className="container-fluid px-4 px-lg-5">
        <div style={{ marginBottom: '28px' }}>
          <h1 style={{ fontFamily: 'var(--font-display)', fontSize: '28px', fontWeight: 800, marginBottom: '4px' }}>Admin Dashboard 🛡️</h1>
          <p style={{ color: 'var(--gray)', fontSize: '15px', marginBottom: 0 }}>Manage users, payments, and platform activity</p>
        </div>

        {/* Stats */}
        <div className="row g-3 mb-4">
          <div className="col-6 col-md-3"><StatCard icon={FiUsers} label="Total Tutors" value={stats?.tutors} color="var(--primary)" bg="#EEF2FF" /></div>
          <div className="col-6 col-md-3"><StatCard icon={FiUsers} label="Total Students" value={stats?.students} color="var(--success)" bg="#ECFDF5" /></div>
          <div className="col-6 col-md-3"><StatCard icon={FiClock} label="Pending Approvals" value={stats?.pending} color="#D97706" bg="#FFF7ED" /></div>
          <div className="col-6 col-md-3"><StatCard icon={FiTrendingUp} label="Total Revenue" value={stats?.revenue ? `₹${stats.revenue.toLocaleString()}` : '₹0'} color="var(--danger)" bg="#FEF2F2" /></div>
        </div>

        {/* Tabs */}
        <div style={{ background: 'white', borderRadius: '20px', overflow: 'hidden', boxShadow: 'var(--shadow-sm)' }}>
          <div style={{ display: 'flex', borderBottom: '1px solid var(--border)', padding: '0 24px' }}>
            {[
              { id: 'pending', label: `Pending Approvals ${pending.length > 0 ? `(${pending.length})` : ''}` },
              { id: 'payments', label: `Payments ${payments.length > 0 ? `(${payments.length})` : ''}` },
            ].map(t => (
              <button key={t.id} onClick={() => setTab(t.id)} style={{
                padding: '16px 20px', border: 'none', background: 'transparent', cursor: 'pointer',
                fontFamily: 'var(--font-body)', fontSize: '14px', fontWeight: 600, transition: 'all 0.2s',
                color: tab === t.id ? 'var(--primary)' : 'var(--gray)',
                borderBottom: tab === t.id ? '2px solid var(--primary)' : '2px solid transparent',
                marginBottom: '-1px',
              }}>{t.label}</button>
            ))}
          </div>

          <div style={{ padding: '24px' }}>
            {tab === 'pending' && (
              <>
                {pending.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '48px', color: 'var(--gray)' }}>
                    <div style={{ fontSize: '48px', marginBottom: '12px' }}>✅</div>
                    <p style={{ fontSize: '15px', fontWeight: 500 }}>All caught up! No pending approvals.</p>
                  </div>
                ) : (
                  <div className="table-responsive">
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                      <thead>
                        <tr style={{ borderBottom: '1px solid var(--border)' }}>
                          {['User', 'Role', 'City', 'Registered', 'Action'].map(h => (
                            <th key={h} style={{ padding: '10px 12px', textAlign: 'left', fontSize: '12px', fontWeight: 700, color: 'var(--gray)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {pending.map(u => (
                          <tr key={u._id} style={{ borderBottom: '1px solid var(--border)' }}>
                            <td style={{ padding: '14px 12px' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <div style={{ width: '36px', height: '36px', borderRadius: '10px', background: 'linear-gradient(135deg, var(--primary), var(--primary-dark))', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: '12px', fontWeight: 800, fontFamily: 'var(--font-display)' }}>
                                  {u.name.charAt(0).toUpperCase()}
                                </div>
                                <div>
                                  <div style={{ fontWeight: 600, fontSize: '14px' }}>{u.name}</div>
                                  <div style={{ fontSize: '12px', color: 'var(--gray)' }}>{u.email}</div>
                                </div>
                              </div>
                            </td>
                            <td style={{ padding: '14px 12px' }}>
                              <span className={`badge-role badge-${u.role}`}>{u.role}</span>
                            </td>
                            <td style={{ padding: '14px 12px', color: 'var(--gray)', fontSize: '13px' }}>{u.city}</td>
                            <td style={{ padding: '14px 12px', color: 'var(--gray)', fontSize: '13px' }}>{new Date(u.createdAt).toLocaleDateString('en-IN')}</td>
                            <td style={{ padding: '14px 12px' }}>
                              <div style={{ display: 'flex', gap: '8px' }}>
                                <button onClick={() => handleApprove(u._id, u.name)} style={{ display: 'flex', alignItems: 'center', gap: '5px', padding: '6px 14px', background: '#ECFDF5', color: 'var(--success)', border: '1px solid #A7F3D0', borderRadius: '8px', fontSize: '12px', fontWeight: 600, cursor: 'pointer', fontFamily: 'var(--font-body)' }}>
                                  <FiCheck size={13} /> Approve
                                </button>
                                <button onClick={() => handleReject(u._id, u.name)} style={{ display: 'flex', alignItems: 'center', gap: '5px', padding: '6px 14px', background: '#FEF2F2', color: 'var(--danger)', border: '1px solid #FECACA', borderRadius: '8px', fontSize: '12px', fontWeight: 600, cursor: 'pointer', fontFamily: 'var(--font-body)' }}>
                                  <FiX size={13} /> Reject
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </>
            )}

            {tab === 'payments' && (
              <>
                {payments.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '48px', color: 'var(--gray)' }}>
                    <div style={{ fontSize: '48px', marginBottom: '12px' }}>💳</div>
                    <p style={{ fontSize: '15px', fontWeight: 500 }}>No payments yet.</p>
                  </div>
                ) : (
                  <div className="table-responsive">
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                      <thead>
                        <tr style={{ borderBottom: '1px solid var(--border)' }}>
                          {['Paid By', 'Unlocked', 'Amount', 'Date', 'Status'].map(h => (
                            <th key={h} style={{ padding: '10px 12px', textAlign: 'left', fontSize: '12px', fontWeight: 700, color: 'var(--gray)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {payments.map(p => (
                          <tr key={p._id} style={{ borderBottom: '1px solid var(--border)' }}>
                            <td style={{ padding: '14px 12px' }}>
                              <div style={{ fontWeight: 600 }}>{p.paidBy?.name}</div>
                              <div style={{ fontSize: '12px', color: 'var(--gray)', textTransform: 'capitalize' }}>{p.paidBy?.role}</div>
                            </td>
                            <td style={{ padding: '14px 12px', fontWeight: 500 }}>{p.unlockedUser?.name}</td>
                            <td style={{ padding: '14px 12px', fontWeight: 700, color: 'var(--success)' }}>₹{p.amount / 100}</td>
                            <td style={{ padding: '14px 12px', color: 'var(--gray)', fontSize: '13px' }}>{new Date(p.createdAt).toLocaleDateString('en-IN')}</td>
                            <td style={{ padding: '14px 12px' }}>
                              <span className={`badge-role badge-${p.status}`}>{p.status}</span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
